/**
 * Live crashpad-wide bed availability for the public site.
 *
 * The eight beds are fixed inventory; their availability is live. We read it
 * from the public flyer endpoint (room slugs + totals) and the per-room bed
 * endpoint, then flatten into a single sorted list grouped by bed size. If the
 * API can't be reached we fall back to the canonical inventory so the page
 * still renders (status unknown → shown as available).
 */

import {useEffect, useState} from 'react'
import {api, PublicBedStatus} from '../../services/api'
import {BED_INVENTORY, BedType} from '../../types/domain'

export interface CrashpadBed {
  code: string
  type: BedType
  price: number
  /** A bookable bed: AVAILABLE and free of any active reservation. */
  available: boolean
  /** Granular status for the listing label (Reserved / Occupied / Unavailable). */
  status: PublicBedStatus
  /** The room slug to deep-link the reserve action to. */
  slug: string | null
  /** Uploaded photo key for this bed (resolve via listingImageSrc). */
  photo: string | null
}

export interface CrashpadAvailability {
  beds: CrashpadBed[]
  totalBeds: number
  availableBeds: number
  loading: boolean
  /** False when we fell back to static inventory (API unreachable). */
  live: boolean
}

/** Sort 1T, 2F, 3T … by the numeric prefix on the bed code. */
const byCode = (a: {code: string}, b: {code: string}) =>
  parseInt(a.code, 10) - parseInt(b.code, 10)

const FALLBACK: CrashpadBed[] = BED_INVENTORY.map((b) => ({
  code: b.code,
  type: b.type,
  price: b.pricePerMonth,
  available: true,
  status: 'AVAILABLE',
  slug: null,
  photo: null,
}))

export function useCrashpadAvailability(): CrashpadAvailability {
  const [state, setState] = useState<CrashpadAvailability>({
    beds: FALLBACK,
    totalBeds: BED_INVENTORY.length,
    availableBeds: BED_INVENTORY.length,
    loading: true,
    live: false,
  })

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const flyer = await api.publicFlyer()
        const perRoom = await Promise.all(
          flyer.rooms.map((r) =>
            api
              .roomBeds(r.slug)
              .then((beds) => beds.map((bed) => ({bed, slug: r.slug})))
              .catch(() => []),
          ),
        )
        if (!alive) return
        const beds: CrashpadBed[] = perRoom
          .flat()
          .map(({bed, slug}) => ({
            code: bed.code,
            type: bed.type as BedType,
            price: Number(bed.pricePerMonth),
            available: bed.available,
            status: bed.status,
            slug,
            photo: bed.photo ?? null,
          }))
          .sort(byCode)

        // Prefer live beds; if the flyer returned no rooms/beds, keep fallback.
        setState({
          beds: beds.length ? beds : FALLBACK,
          totalBeds: flyer.totals.totalBeds || BED_INVENTORY.length,
          availableBeds: beds.length ? beds.filter((b) => b.available).length : flyer.totals.availableBeds,
          loading: false,
          live: beds.length > 0,
        })
      } catch {
        if (alive) setState((s) => ({...s, loading: false, live: false}))
      }
    })()
    return () => {
      alive = false
    }
  }, [])

  return state
}
